import { useCallback, useEffect, useRef, useState } from "react";
import { ChatMessage, ChatSession, ConversationStore } from "../types";

const newSessionId = () => `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const makeSession = (agentType: string): ChatSession => {
	const now = new Date().toISOString();
	return {
		id: newSessionId(),
		name: "New Chat",
		createdAt: now,
		lastActivityAt: now,
		agentType,
		messages: [],
	};
};

interface RecordExchangeArgs {
	userMessage: string;
	agentMessage: string;
	remoteSessionId?: string;
	rawResponse?: unknown;
}

interface UseChatSessionsArgs {
	agentType: string;
	store: ConversationStore;
}

export const useChatSessions = ({ agentType, store }: UseChatSessionsArgs) => {
	const [sessions, setSessions] = useState<ChatSession[]>([]);
	const [currentSessionId, setCurrentSessionId] = useState<string>("");

	// Latest sessions, readable inside async callbacks without stale closures.
	const sessionsRef = useRef(sessions);
	sessionsRef.current = sessions;

	// In-flight create per client session id, so two quick exchanges don't create
	// two server sessions (the second waits for the first's id, then appends).
	const pendingCreates = useRef<Map<string, Promise<string>>>(new Map());

	const createSession = useCallback((): string => {
		const session = makeSession(agentType);
		setSessions((prev) => [session, ...prev]);
		setCurrentSessionId(session.id);
		return session.id;
	}, [agentType]);

	const switchSession = useCallback((id: string) => {
		setCurrentSessionId(id);
	}, []);

	const deleteSession = useCallback(
		(id: string) => {
			const target = sessionsRef.current.find((s) => s.id === id);
			if (target?.serverId) Promise.resolve(store.remove(target.serverId)).catch(() => undefined);

			setSessions((prev) => {
				const next = prev.filter((s) => s.id !== id);
				if (id === currentSessionId) {
					const [first] = next;
					if (first) {
						setCurrentSessionId(first.id);
					} else {
						const fresh = makeSession(agentType);
						setCurrentSessionId(fresh.id);
						return [fresh];
					}
				}
				return next;
			});
		},
		[agentType, currentSessionId, store]
	);

	const renameSession = useCallback(
		(id: string, name: string) => {
			const trimmed = name.trim();
			if (!trimmed) return;
			setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, name: trimmed } : s)));
			const target = sessionsRef.current.find((s) => s.id === id);
			if (target?.serverId && store.rename) {
				Promise.resolve(store.rename(target.serverId, trimmed)).catch(() => undefined);
			}
		},
		[store]
	);

	const toggleFavourite = useCallback(
		(id: string) => {
			const target = sessionsRef.current.find((s) => s.id === id);
			const next = !target?.favourite;
			setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, favourite: next } : s)));
			if (target?.serverId && store.setFavourite) {
				Promise.resolve(store.setFavourite(target.serverId, next)).catch(() => undefined);
			}
		},
		[store]
	);

	const appendMessages = useCallback((id: string, msgs: ChatMessage[], remoteSessionId?: string) => {
		setSessions((prev) =>
			prev.map((s) => {
				if (s.id !== id) return s;
				const messages = [...s.messages, ...msgs];
				let name = s.name;
				if (name === "New Chat") {
					const firstUser = messages.find((m) => m.role === "user");
					if (firstUser) name = firstUser.content.slice(0, 50) + (firstUser.content.length > 50 ? "..." : "");
				}
				return {
					...s,
					name,
					messages,
					lastActivityAt: new Date().toISOString(),
					remoteSessionId: remoteSessionId || s.remoteSessionId,
				};
			})
		);
	}, []);

	const updateMessage = useCallback(
		(id: string, messageId: string, patch: Partial<ChatMessage>, remoteSessionId?: string) => {
			setSessions((prev) =>
				prev.map((s) =>
					s.id !== id
						? s
						: {
								...s,
								messages: s.messages.map((m) => (m.id === messageId ? { ...m, ...patch } : m)),
								remoteSessionId: remoteSessionId || s.remoteSessionId,
							}
				)
			);
		},
		[]
	);

	// Persist one completed exchange: create the server session on the first turn
	// (capturing its id), append on subsequent turns. Optimistic — failures are swallowed.
	const recordExchange = useCallback(
		async (id: string, { userMessage, agentMessage, remoteSessionId, rawResponse }: RecordExchangeArgs) => {
			const session = sessionsRef.current.find((s) => s.id === id);
			if (!session) return;
			const remote = remoteSessionId ?? session.remoteSessionId;
			// The backend keys sessions by remote_session_id; without one we can't persist coherently.
			if (!remote) return;

			try {
				if (session.serverId) {
					await store.recordExchange({
						sessionId: session.serverId,
						remoteSessionId: remote,
						userMessage,
						agentMessage,
						rawResponse,
					});
					return;
				}

				let createP = pendingCreates.current.get(id);
				if (createP) {
					// Create already in flight for this session — wait for its id, then append.
					const serverId = await createP;
					await store.recordExchange({ sessionId: serverId, remoteSessionId: remote, userMessage, agentMessage, rawResponse });
					return;
				}

				createP = store
					.recordExchange({ title: session.name, remoteSessionId: remote, userMessage, agentMessage, rawResponse })
					.then((r) => r.sessionId);
				pendingCreates.current.set(id, createP);
				createP.catch(() => pendingCreates.current.delete(id));

				const serverId = await createP;
				setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, serverId } : s)));
			} catch {
				// Optimistic UI already reflects the exchange; ignore persistence errors.
			}
		},
		[store]
	);

	// Load from the store on mount, and again whenever the store changes — e.g. when the auth
	// token (and thus the user's email) arrives after first render, which is the norm in SSR
	// apps that read the token from localStorage in an effect. The cancelled flag prevents a
	// superseded load from clobbering a newer one.
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			let loaded: ChatSession[] = [];
			try {
				loaded = await store.list();
			} catch {
				loaded = [];
			}
			if (cancelled) return;
			const sorted = [...loaded].sort(
				(a, b) => new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime()
			);
			const [mostRecent] = sorted;
			if (mostRecent) {
				setSessions(sorted);
				setCurrentSessionId(mostRecent.id);
			} else {
				const fresh = makeSession(agentType);
				setSessions([fresh]);
				setCurrentSessionId(fresh.id);
			}
		};
		load();
		return () => {
			cancelled = true;
		};
	}, [agentType, store]);

	const currentSession = sessions.find((s) => s.id === currentSessionId);

	return {
		sessions,
		currentSession,
		currentSessionId,
		createSession,
		switchSession,
		deleteSession,
		renameSession,
		toggleFavourite,
		appendMessages,
		updateMessage,
		recordExchange,
	};
};
